import { Add, Delete, Edit, History } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    IconButton,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';

function formatDate(value) {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '—';
    }
    return date.toLocaleDateString('pt-BR');
}

function DesktopReleasesAdminPage() {
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checking, setChecking] = useState(!isMasterUser());
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setChecking(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const fetchReleases = useCallback(() => {
        if (!isMaster) {
            return;
        }
        setLoading(true);
        api.get('/desktop-releases')
            .then((response) => {
                setReleases(Array.isArray(response?.data?.data) ? response.data.data : []);
            })
            .catch(() => setReleases([]))
            .finally(() => setLoading(false));
    }, [isMaster]);

    useEffect(() => {
        fetchReleases();
    }, [fetchReleases]);

    const handleDelete = async (release) => {
        const result = await Swal.fire({
            title: 'Remover versão?',
            text: `A release ${release.version} será removida do histórico.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remover',
            cancelButtonText: 'Cancelar',
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await api.delete(`/desktop-releases/${release.id}`);
            fetchReleases();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Falha ao remover',
                text: error?.response?.data?.message || error?.response?.data?.error || 'Tente novamente.',
            });
        }
    };

    if (checking) {
        return <Loading />;
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Versões Desktop</Typography>
            </Breadcrumbs>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2} mb={2}>
                <Box>
                    <Typography variant="h5">Farm Link — versões</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Cadastre a versão mais recente e o histórico com links por sistema operacional.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<History />} onClick={() => navigate('/admin/farm-link-changelog')}>
                        Ver histórico público
                    </Button>
                    <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/admin/desktop-releases/new')}>
                        Nova versão
                    </Button>
                </Stack>
            </Stack>

            {loading ? (
                <Loading />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Versão</TableCell>
                                <TableCell>Título</TableCell>
                                <TableCell>Publicada</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Downloads</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {releases.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Nenhuma versão cadastrada. Publique a primeira release.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                releases.map((release) => (
                                    <TableRow key={release.id} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography fontWeight={700}>{release.version}</Typography>
                                                {release.active !== false
                                                    && release.id === releases.find((item) => item.active !== false)?.id
                                                    && (
                                                        <Chip size="small" color="primary" label="Atual" />
                                                    )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{release.title}</TableCell>
                                        <TableCell>{formatDate(release.published_at)}</TableCell>
                                        <TableCell>
                                            {release.active !== false ? (
                                                <Chip size="small" color="success" label="Ativa" />
                                            ) : (
                                                <Chip size="small" label="Inativa" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {release.download_windows && <Chip size="small" label="Windows" />}
                                                {release.download_mac && <Chip size="small" label="Mac" />}
                                                {release.download_linux && <Chip size="small" label="Linux" />}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar">
                                                <IconButton onClick={() => navigate(`/admin/desktop-releases/${release.id}/edit`)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Remover">
                                                <IconButton onClick={() => handleDelete(release)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default DesktopReleasesAdminPage;
